import { RESERVATION_UNIT_OPTIONS } from './courtTypes'
import {
  DAY_OF_WEEK_ORDER,
  type CourtOperatingHourSaveInput,
  type CourtOperatingHourView,
} from './operatingHourTypes'

/**
 * 코트 운영시간 설정 폼 상태 ↔ `CourtOperatingHourSaveInput` 매핑 + 사전 검증(#600 §3 미러링, 설계 §2-3).
 *
 * 🚨 `if (status === ...)` 를 컴포넌트에 흩지 않는다 — `courtViewModel.ts` 가 세운 규칙 승계.
 */

/** 운영시간을 아직 설정하지 않은 코트의 초기 기본값(Figma 실측 예시값 — 검증에는 영향 없다). */
const DEFAULT_START_TIME = '06:00'
const DEFAULT_END_TIME = '22:00'

export interface OperatingHourDayFormState {
  dayOfWeek: string
  dayOfWeekLabel: string
  active: boolean
  startTime: string
  endTime: string
}

/** 숫자 입력은 빈 문자열이 유효한 중간 상태라 텍스트로 다룬다 — `courtViewModel.ts` 규칙 승계. */
export interface OperatingHourFormState {
  reservationUnitMinutes: number
  maxReservationMinutes: string
  /** "요일별 운영시간 다르게 설정" 토글. 서버에 저장하지 않는 순수 UI 상태(설계 §2-2 자율 결정). */
  differsByDay: boolean
  /** `differsByDay=false` 일 때 전 요일에 적용되는 공통 시작·종료. */
  uniformStartTime: string
  uniformEndTime: string
  /** `differsByDay=true` 일 때만 편집 대상. 항상 7행, 월→일 고정. */
  days: OperatingHourDayFormState[]
}

/** 서버 응답 → 폼 초기값. 토글의 초기 on/off 는 `sameForAllDays && 전 요일 active` 로 역산한다. */
export function toOperatingHourFormState(view: CourtOperatingHourView): OperatingHourFormState {
  const allDaysActive = view.days.every((day) => day.active)
  const differsByDay = !(view.sameForAllDays && allDaysActive)

  return {
    reservationUnitMinutes: view.reservationUnitMinutes,
    maxReservationMinutes: String(view.maxReservationMinutes),
    differsByDay,
    uniformStartTime: view.operatingStartTime ?? DEFAULT_START_TIME,
    uniformEndTime: view.operatingEndTime ?? DEFAULT_END_TIME,
    days: view.days.map((day) => ({
      dayOfWeek: day.dayOfWeek,
      dayOfWeekLabel: day.dayOfWeekLabel,
      active: day.active,
      startTime: day.startTime ?? view.operatingStartTime ?? DEFAULT_START_TIME,
      endTime: day.endTime ?? view.operatingEndTime ?? DEFAULT_END_TIME,
    })),
  }
}

/**
 * "요일별 다르게 설정" 토글 전환. 꺼짐→켜짐 전환 시, 그 시점의 공통 시작·종료를 7일 전체(active)에
 * 복사해 넣는다 — 꺼짐 모드는 "전 요일이 공통 시각으로 운영 중"이라는 상태와 동치이기 때문이다.
 */
export function applyDiffersByDayToggle(
  form: OperatingHourFormState,
  next: boolean
): OperatingHourFormState {
  if (next === form.differsByDay) return form
  if (!next) return { ...form, differsByDay: false }

  return {
    ...form,
    differsByDay: true,
    days: form.days.map((day) => ({
      ...day,
      active: true,
      startTime: form.uniformStartTime,
      endTime: form.uniformEndTime,
    })),
  }
}

/** 폼 → `PUT` 요청 바디. 검증을 통과한 뒤에만 호출한다(숫자 파싱 실패를 가정하지 않는다). */
export function toOperatingHourSaveInput(
  form: OperatingHourFormState
): CourtOperatingHourSaveInput {
  const days = form.differsByDay
    ? form.days.map((day) =>
        day.active
          ? {
              dayOfWeek: day.dayOfWeek,
              active: true,
              startTime: day.startTime,
              endTime: day.endTime,
            }
          : { dayOfWeek: day.dayOfWeek, active: false }
      )
    : DAY_OF_WEEK_ORDER.map((dayOfWeek) => ({
        dayOfWeek,
        active: true,
        startTime: form.uniformStartTime,
        endTime: form.uniformEndTime,
      }))

  return {
    reservationUnitMinutes: form.reservationUnitMinutes,
    maxReservationMinutes: Number(form.maxReservationMinutes),
    days,
  }
}

/** #600 §1 이 라벨을 선택된 값 하나만 내려줘서, 선택지 각각의 표시 라벨은 화면이 분을 문장으로 조립한다
 *  (선택지 값 자체는 `allowedReservationUnitMinutes` 그대로 — 조립하는 것은 라벨 문구뿐). */
function formatReservationUnitLabel(minutes: number): string {
  const known = RESERVATION_UNIT_OPTIONS.find((option) => option.value === minutes)
  if (known) return known.label
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  if (hours === 0) return `${remainder}분`
  return remainder === 0 ? `${hours}시간` : `${hours}시간 ${remainder}분`
}

export function toReservationUnitOptions(
  view: CourtOperatingHourView
): { value: number; label: string }[] {
  return view.allowedReservationUnitMinutes.map((value) => ({
    value,
    label: formatReservationUnitLabel(value),
  }))
}

/** "HH:mm" → 자정으로부터의 분. "24:00" 은 1440. */
function toMinutes(time: string): number {
  const [hoursText, minutesText] = time.split(':')
  return Number(hoursText ?? 0) * 60 + Number(minutesText ?? 0)
}

export interface OperatingHourFormErrors {
  maxReservationMinutes?: string
  /** `differsByDay=false` 일 때의 공통 시작·종료 오류. */
  uniformTime?: string
  /** 활성 요일 0개 등 요일 목록 전역 오류(`differsByDay=true` 전용). */
  daysGeneral?: string
  /** `dayOfWeek` → 그 요일의 시각 오류(`differsByDay=true` 전용). */
  dayErrors: Partial<Record<string, string>>
}

function validateMaxReservationMinutes(value: string, unitMinutes: number): string | undefined {
  const trimmed = value.trim()
  if (trimmed.length === 0) return '최대 예약 시간을 입력해 주세요.'
  if (!/^\d+$/.test(trimmed)) return '최대 예약 시간은 분 단위 정수로 입력해 주세요.'
  const minutes = Number(trimmed)
  if (minutes < unitMinutes)
    return `최대 예약 시간은 예약 단위(${unitMinutes}분) 이상이어야 합니다.`
  if (minutes % unitMinutes !== 0)
    return `최대 예약 시간은 예약 단위(${unitMinutes}분)의 배수여야 합니다.`
  return undefined
}

/** 종료 > 시작 & 구간 길이 ≥ 예약 단위(#600 §3 V1·V7). 통과하면 `undefined`. */
function validateTimeRange(
  startTime: string,
  endTime: string,
  unitMinutes: number,
  dayLabel?: string
): string | undefined {
  const start = toMinutes(startTime)
  const end = toMinutes(endTime)
  const prefix = dayLabel ? `${dayLabel} ` : ''
  if (end <= start) return `${prefix}운영 종료 시각은 시작 시각보다 뒤여야 합니다.`
  if (end - start < unitMinutes)
    return `${prefix}운영시간이 예약 단위(${unitMinutes}분)보다 짧습니다.`
  return undefined
}

const ALL_DAYS_OFF_NOTICE =
  "운영 요일을 최소 하루 이상 선택해 주세요. 코트를 운영하지 않으려면 코트 목록에서 코트 상태를 '일시 중단'으로 변경해 주세요."

/** 클라이언트 사전 검증(#600 §3 V4~V8 미러링). 서버가 최종 판정자다. */
export function validateOperatingHourForm(form: OperatingHourFormState): OperatingHourFormErrors {
  const errors: OperatingHourFormErrors = { dayErrors: {} }

  const maxReservationError = validateMaxReservationMinutes(
    form.maxReservationMinutes,
    form.reservationUnitMinutes
  )
  if (maxReservationError) errors.maxReservationMinutes = maxReservationError

  if (!form.differsByDay) {
    const timeError = validateTimeRange(
      form.uniformStartTime,
      form.uniformEndTime,
      form.reservationUnitMinutes
    )
    if (timeError) errors.uniformTime = timeError
    return errors
  }

  const activeDays = form.days.filter((day) => day.active)
  if (activeDays.length === 0) {
    errors.daysGeneral = ALL_DAYS_OFF_NOTICE
    return errors
  }

  for (const day of activeDays) {
    const dayError = validateTimeRange(
      day.startTime,
      day.endTime,
      form.reservationUnitMinutes,
      day.dayOfWeekLabel
    )
    if (dayError) errors.dayErrors[day.dayOfWeek] = dayError
  }

  return errors
}

export function hasOperatingHourFormErrors(errors: OperatingHourFormErrors): boolean {
  return (
    Boolean(errors.maxReservationMinutes || errors.uniformTime || errors.daysGeneral) ||
    Object.keys(errors.dayErrors).length > 0
  )
}
